import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useTurnkey } from "@turnkey/react-wallet-kit";
import { OtpType } from "@turnkey/core";
import { validateAndLowerCase, type Country, COUNTRIES } from "@orb-labs/types";
import { capture, otpRateLimiter } from "@orb-labs/orby-react";
import { AuthEvents, WEB_FONT_STACK_MONO } from "@orb-labs/orby-react/bullrun";
import { BULLRUN_COLORS } from "@/theme";
import { getStoredAttribution } from "@/observability";

const normalizeRateLimitKey = (email: string): string => validateAndLowerCase(email) ?? "";

const getDefaultCountry = (): Country => {
  try {
    const locale = navigator.language || "en-US";
    const code = locale.split("-")[1] || "US";
    return COUNTRIES.find((c) => c.code === code.toUpperCase()) || COUNTRIES[0];
  } catch {
    return COUNTRIES[0];
  }
};

enum AuthView {
  Options = "options",
  EmailInput = "emailInput",
  PhoneInput = "phoneInput",
  OtpCode = "otpCode",
  PhoneOtpCode = "phoneOtpCode",
  WalletPicker = "walletPicker",
}

export type AuthModalInitialView = "options" | "walletPicker";

interface AuthModalProps {
  visible: boolean;
  onClose: () => void;
  initialView?: AuthModalInitialView;
}

// Splitting the 595-line AuthModal into sub-views is a separate refactor.
// eslint-disable-next-line complexity
export function AuthModal({ visible, onClose, initialView = "options" }: AuthModalProps) {
  // NOTE: The wallet-picker flow is currently disabled (see the commented JSX
  // blocks around lines ~590 and ~420). Wallet-related state, helpers, and
  // styled components were removed to keep the file typecheck-clean. Restore
  // from git history when re-enabling the flow.
  const { initOtp, completeOtp, handleGoogleOauth, fetchWalletProviders } = useTurnkey();

  const [view, setView] = useState<AuthView>(AuthView.Options);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country>(getDefaultCountry);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpId, setOtpId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const countrySearchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = useMemo(() => {
    const trimmed = countrySearch.trim();
    if (!trimmed) return COUNTRIES;
    // `validateAndLowerCase` cannot be used on free-form search input because
    // it throws for solana-address-shaped strings (32–44 char base58) — a
    // crash path if a user pastes an address into the country picker. These
    // are plain display strings, not onchain addresses, so direct
    // `.toLowerCase()` is safe.
    // eslint-disable-next-line no-restricted-syntax -- see comment above
    const search = trimmed.toLowerCase();
    return COUNTRIES.filter((c) => {
      // eslint-disable-next-line no-restricted-syntax -- see comment above
      const name = c.name.toLowerCase();
      // eslint-disable-next-line no-restricted-syntax -- see comment above
      const code = c.code.toLowerCase();
      return name.includes(search) || c.dialCode.includes(search) || code.includes(search);
    });
  }, [countrySearch]);

  const resetState = useCallback(() => {
    setView(initialView === "walletPicker" ? AuthView.WalletPicker : AuthView.Options);
    setEmail("");
    setPhoneNumber("");
    setOtpCode("");
    setOtpId(null);
    setIsSubmitting(false);
    setError(null);
    setShowCountryPicker(false);
    setCountrySearch("");
  }, [initialView]);

  const handleClose = useCallback(() => {
    capture(AuthEvents.AUTH_SHEET_CLOSED, { view });
    resetState();
    onClose();
  }, [resetState, onClose, view]);

  useEffect(() => {
    if (!visible) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [visible, handleClose]);

  useEffect(() => {
    if (visible) {
      capture(AuthEvents.AUTH_SHEET_OPENED, { initial_view: initialView });
    }
    if (visible && initialView === "walletPicker") {
      setView(AuthView.WalletPicker);
      handleShowWallets();
    }
    // Intentionally gate on `visible` only — re-running this effect when
    // `initialView` or `handleShowWallets` change would fire the analytics
    // event twice per sheet open.
  }, [visible]);

  useEffect(() => {
    if (view === AuthView.EmailInput) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    } else if (view === AuthView.PhoneInput) {
      setTimeout(() => phoneInputRef.current?.focus(), 100);
    } else if (view === AuthView.OtpCode || view === AuthView.PhoneOtpCode) {
      setTimeout(() => otpInputRef.current?.focus(), 100);
    }
  }, [view]);

  useEffect(() => {
    if (showCountryPicker) {
      setTimeout(() => countrySearchRef.current?.focus(), 100);
    }
  }, [showCountryPicker]);

  const handleGoogleLogin = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    capture(AuthEvents.AUTH_METHOD_SELECTED, { method: "google" });
    capture(AuthEvents.OAUTH_INITIATED, { provider: "google" });
    try {
      // Do NOT close the modal before OAuth starts. If the SDK uses a redirect,
      // the page navigates away and the modal is irrelevant. If it throws before
      // redirecting (config error, popup blocked, etc.), the modal stays open so
      // the user sees the error.
      await handleGoogleOauth();
      capture(AuthEvents.OAUTH_SUCCESS, { provider: "google" });
      capture(AuthEvents.LOGIN_SUCCESS, { method: "google", attribution: getStoredAttribution() });
      handleClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Google login failed";
      capture(AuthEvents.OAUTH_FAILED, { provider: "google", error_message: msg });
      setError(msg);
      setIsSubmitting(false);
    }
  }, [handleGoogleOauth, handleClose]);

  const handleSendOtp = useCallback(async () => {
    if (!email.includes("@") || !email.includes(".")) {
      setError("Please enter a valid email address");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    capture(AuthEvents.EMAIL_OTP_REQUESTED, {});
    try {
      const id = await initOtp({ contact: email, otpType: OtpType.Email });
      if (!id) throw new Error("Failed to send verification code");
      setOtpId(id);
      setView(AuthView.OtpCode);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to send code";
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, initOtp]);

  const handleVerifyOtp = useCallback(async () => {
    if (otpCode.length !== 6 || !otpId) return;

    const rateLimitKey = normalizeRateLimitKey(email);
    const rateLimitCheck = otpRateLimiter.canAttempt(rateLimitKey);
    if (!rateLimitCheck.allowed) {
      const lockoutTime = otpRateLimiter.formatLockoutTime(rateLimitCheck.lockoutRemainingMs || 0);
      capture(AuthEvents.RATE_LIMIT_HIT, {
        action: "otp_verification",
        lockout_time_remaining_ms: rateLimitCheck.lockoutRemainingMs,
      });
      setError(`Too many attempts. Try again in ${lockoutTime}.`);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      otpRateLimiter.recordAttempt(rateLimitKey);
      const result = await completeOtp({
        otpId,
        otpCode,
        contact: email,
        otpType: OtpType.Email,
      });
      otpRateLimiter.clearAttempts(rateLimitKey);
      capture(AuthEvents.EMAIL_OTP_VERIFIED, {});

      const isNewUser = result?.action === "SIGNUP";
      const attribution = getStoredAttribution();
      if (isNewUser) {
        capture(AuthEvents.SIGNUP_SUCCESS, { method: "email", attribution });
      }
      capture(AuthEvents.LOGIN_SUCCESS, { method: "email", is_new_user: isNewUser, attribution });
      handleClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Verification failed";
      const { remainingAttempts } = otpRateLimiter.canAttempt(rateLimitKey);
      capture(AuthEvents.EMAIL_OTP_VERIFY_FAILED, { error_message: msg, attempts_remaining: remainingAttempts });
      capture(AuthEvents.LOGIN_FAILED, { method: "email", error_message: msg });
      const attemptSuffix =
        remainingAttempts > 0 ? ` ${remainingAttempts} attempt${remainingAttempts === 1 ? "" : "s"} remaining.` : "";
      setError(`${msg}.${attemptSuffix}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [otpCode, otpId, email, completeOtp, handleClose]);

  const getFullPhoneNumber = useCallback(
    () => `${selectedCountry.dialCode}${phoneNumber.replace(/\D/g, "")}`,
    [selectedCountry.dialCode, phoneNumber]
  );

  const handleSendSmsOtp = useCallback(async () => {
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    if (digitsOnly.length < 6) {
      setError("Please enter a valid phone number");
      return;
    }
    const fullPhone = `${selectedCountry.dialCode}${digitsOnly}`;
    setError(null);
    setIsSubmitting(true);
    capture(AuthEvents.PHONE_OTP_REQUESTED, {});
    try {
      const id = await initOtp({ contact: fullPhone, otpType: OtpType.Sms });
      if (!id) throw new Error("Failed to send verification code");
      setOtpId(id);
      setView(AuthView.PhoneOtpCode);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to send code";
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [phoneNumber, selectedCountry.dialCode, initOtp]);

  const handleVerifySmsOtp = useCallback(async () => {
    if (otpCode.length !== 6 || !otpId) return;

    const fullPhone = getFullPhoneNumber();
    const rateLimitCheck = otpRateLimiter.canAttempt(fullPhone);
    if (!rateLimitCheck.allowed) {
      const lockoutTime = otpRateLimiter.formatLockoutTime(rateLimitCheck.lockoutRemainingMs || 0);
      capture(AuthEvents.RATE_LIMIT_HIT, {
        action: "otp_verification",
        lockout_time_remaining_ms: rateLimitCheck.lockoutRemainingMs,
      });
      setError(`Too many attempts. Try again in ${lockoutTime}.`);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      otpRateLimiter.recordAttempt(fullPhone);
      const result = await completeOtp({
        otpId,
        otpCode,
        contact: fullPhone,
        otpType: OtpType.Sms,
      });
      otpRateLimiter.clearAttempts(fullPhone);
      capture(AuthEvents.PHONE_OTP_VERIFIED, {});

      const isNewUser = result?.action === "SIGNUP";
      const attribution = getStoredAttribution();
      if (isNewUser) {
        capture(AuthEvents.SIGNUP_SUCCESS, { method: "phone", attribution });
      }
      capture(AuthEvents.LOGIN_SUCCESS, { method: "phone", is_new_user: isNewUser, attribution });
      handleClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Verification failed";
      const { remainingAttempts } = otpRateLimiter.canAttempt(fullPhone);
      capture(AuthEvents.PHONE_OTP_VERIFY_FAILED, { error_message: msg, attempts_remaining: remainingAttempts });
      capture(AuthEvents.LOGIN_FAILED, { method: "phone", error_message: msg });
      const attemptSuffix =
        remainingAttempts > 0 ? ` ${remainingAttempts} attempt${remainingAttempts === 1 ? "" : "s"} remaining.` : "";
      setError(`${msg}.${attemptSuffix}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [otpCode, otpId, getFullPhoneNumber, completeOtp, handleClose]);

  const handleShowWallets = useCallback(async () => {
    // Wallet-picker UI is currently commented out; this stays functional so
    // `initialView="walletPicker"` callers don't crash. When the UI is
    // re-enabled, restore the richer handler from git history.
    setError(null);
    setView(AuthView.WalletPicker);
    try {
      await fetchWalletProviders();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to detect wallets";
      setError(msg);
    }
  }, [fetchWalletProviders]);

  const goBack = useCallback(() => {
    setError(null);
    if (view === AuthView.OtpCode) {
      setOtpCode("");
      setView(AuthView.EmailInput);
    } else if (view === AuthView.PhoneOtpCode) {
      setOtpCode("");
      setView(AuthView.PhoneInput);
    } else {
      setView(AuthView.Options);
    }
  }, [view]);

  if (!visible) return null;

  const showBack = view !== AuthView.Options;

  return (
    <Backdrop onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          {showBack ? (
            <BackButton type="button" onClick={goBack}>
              ←
            </BackButton>
          ) : (
            <HeaderSpacer />
          )}
          <ModalTitle>
            {view === AuthView.Options && "Log in"}
            {view === AuthView.EmailInput && "Enter your email"}
            {view === AuthView.PhoneInput && "Enter your phone number"}
            {view === AuthView.OtpCode && "Verify your email"}
            {view === AuthView.PhoneOtpCode && "Verify your phone"}
            {view === AuthView.WalletPicker && "Connect wallet"}
          </ModalTitle>
          <CloseButton type="button" onClick={handleClose}>
            ✕
          </CloseButton>
        </ModalHeader>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        {view === AuthView.Options && (
          <OptionsContainer>
            <AuthOption type="button" onClick={handleGoogleLogin} disabled={isSubmitting}>
              <OptionIcon>
                <GoogleIcon />
              </OptionIcon>
              <OptionLabel>Continue with Google</OptionLabel>
            </AuthOption>

            <AuthOption
              type="button"
              onClick={() => {
                setError(null);
                capture(AuthEvents.AUTH_METHOD_SELECTED, { method: "email" });
                setView(AuthView.EmailInput);
              }}
            >
              <OptionIcon>
                <EmailIcon>@</EmailIcon>
              </OptionIcon>
              <OptionLabel>Continue with Email</OptionLabel>
            </AuthOption>

            <AuthOption
              type="button"
              onClick={() => {
                setError(null);
                capture(AuthEvents.AUTH_METHOD_SELECTED, { method: "phone" });
                setView(AuthView.PhoneInput);
              }}
            >
              <OptionIcon>
                <PhoneIcon />
              </OptionIcon>
              <OptionLabel>Continue with Phone</OptionLabel>
            </AuthOption>

            {/* <Divider>
              <DividerLine />
              <DividerText>or</DividerText>
              <DividerLine />
            </Divider>

            <AuthOption
              type="button"
              onClick={() => {
                capture(AuthEvents.AUTH_METHOD_SELECTED, { method: "wallet" });
                handleShowWallets();
              }}
              disabled={isSubmitting}
            >
              <OptionIcon>
                <WalletIcon />
              </OptionIcon>
              <OptionLabel>Connect Wallet</OptionLabel>
            </AuthOption> */}
          </OptionsContainer>
        )}

        {view === AuthView.EmailInput && (
          <FormContainer>
            <InputLabel>Email address</InputLabel>
            <TextInput
              ref={emailInputRef}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
              disabled={isSubmitting}
              autoComplete="email"
            />
            <PrimaryButton type="button" onClick={handleSendOtp} disabled={isSubmitting || !email.includes("@")}>
              {isSubmitting ? <Spinner /> : "Send verification code"}
            </PrimaryButton>
          </FormContainer>
        )}

        {view === AuthView.OtpCode && (
          <FormContainer>
            <InputLabel>
              Enter the 6-digit code sent to <EmailHighlight>{email}</EmailHighlight>
            </InputLabel>
            <TextInput
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setOtpCode(v);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
              disabled={isSubmitting}
              autoComplete="one-time-code"
              $mono
            />
            <PrimaryButton type="button" onClick={handleVerifyOtp} disabled={isSubmitting || otpCode.length !== 6}>
              {isSubmitting ? <Spinner /> : "Verify"}
            </PrimaryButton>
            <ResendRow>
              <ResendButton type="button" onClick={handleSendOtp} disabled={isSubmitting}>
                Resend code
              </ResendButton>
            </ResendRow>
          </FormContainer>
        )}

        {view === AuthView.PhoneInput && (
          <FormContainer>
            <InputLabel>Phone number</InputLabel>
            <PhoneInputRow>
              <CountryPickerButton type="button" onClick={() => setShowCountryPicker(true)} disabled={isSubmitting}>
                <span>{selectedCountry.flag}</span>
                <span>{selectedCountry.dialCode}</span>
                <DropdownArrow>▼</DropdownArrow>
              </CountryPickerButton>
              <TextInput
                ref={phoneInputRef}
                type="tel"
                inputMode="tel"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s()+-]/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleSendSmsOtp()}
                disabled={isSubmitting}
                autoComplete="tel-national"
                style={{ flex: 1 }}
              />
            </PhoneInputRow>
            {showCountryPicker && (
              <CountryDropdown>
                <CountrySearchInput
                  ref={countrySearchRef}
                  type="text"
                  placeholder="Search country…"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  autoComplete="off"
                />
                <CountryList>
                  {filteredCountries.map((c) => (
                    <CountryItem
                      key={c.code}
                      type="button"
                      $active={c.code === selectedCountry.code}
                      onClick={() => {
                        setSelectedCountry(c);
                        setShowCountryPicker(false);
                        setCountrySearch("");
                        phoneInputRef.current?.focus();
                      }}
                    >
                      <span>{c.flag}</span>
                      <span style={{ flex: 1 }}>{c.name}</span>
                      <CountryDialCode>{c.dialCode}</CountryDialCode>
                    </CountryItem>
                  ))}
                </CountryList>
              </CountryDropdown>
            )}
            <PrimaryButton
              type="button"
              onClick={handleSendSmsOtp}
              disabled={isSubmitting || phoneNumber.replace(/\D/g, "").length < 6}
            >
              {isSubmitting ? <Spinner /> : "Send verification code"}
            </PrimaryButton>
          </FormContainer>
        )}

        {view === AuthView.PhoneOtpCode && (
          <FormContainer>
            <InputLabel>
              Enter the 6-digit code sent to{" "}
              <EmailHighlight>
                {selectedCountry.dialCode} {phoneNumber}
              </EmailHighlight>
            </InputLabel>
            <TextInput
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setOtpCode(v);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleVerifySmsOtp()}
              disabled={isSubmitting}
              autoComplete="one-time-code"
              $mono
            />
            <PrimaryButton type="button" onClick={handleVerifySmsOtp} disabled={isSubmitting || otpCode.length !== 6}>
              {isSubmitting ? <Spinner /> : "Verify"}
            </PrimaryButton>
            <ResendRow>
              <ResendButton type="button" onClick={handleSendSmsOtp} disabled={isSubmitting}>
                Resend code
              </ResendButton>
            </ResendRow>
          </FormContainer>
        )}

        {/* {view === AuthView.WalletPicker && (
          <WalletContainer>
            {walletLoading && (
              <WalletLoading>
                <Spinner />
                <span>Detecting wallets…</span>
              </WalletLoading>
            )}
            {!walletLoading && walletProviders.length === 0 && (
              <EmptyState>No wallets detected. Install a browser wallet extension like MetaMask or Phantom.</EmptyState>
            )}
            {!walletLoading &&
              walletProviders.map((provider, idx) => (
                <WalletOption
                  key={`${provider.info?.name ?? "wallet"}-${provider.chainInfo?.namespace ?? ""}-${idx}`}
                  type="button"
                  onClick={() => handleWalletSelect(provider)}
                  disabled={isSubmitting}
                >
                  {provider.info?.icon ? (
                    <WalletIconImg src={provider.info.icon} alt={provider.info?.name ?? "Wallet"} />
                  ) : (
                    <WalletIconPlaceholder />
                  )}
                  <WalletInfo>
                    <WalletName>{provider.info?.name ?? "Unknown Wallet"}</WalletName>
                  </WalletInfo>
                  <WalletMeta>
                    {provider.connectedAddresses && provider.connectedAddresses.length > 0 && (
                      <ConnectedBadge>Connected</ConnectedBadge>
                    )}
                    <ChainBadge>{provider.chainInfo?.namespace === "ethereum" ? <EthIcon /> : <SolIcon />}</ChainBadge>
                  </WalletMeta>
                </WalletOption>
              ))}
          </WalletContainer>
        )} */}
      </ModalContainer>
    </Backdrop>
  );
}

/* ─── Icons ─── */

const GOOGLE_ICON_RED_PATH =
  "M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 " +
  "6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z";

const GOOGLE_ICON_BLUE_PATH =
  "M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 " +
  "7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z";

const GOOGLE_ICON_YELLOW_PATH =
  "M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 " +
  "16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z";

const GOOGLE_ICON_GREEN_PATH =
  "M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 " +
  "0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d={GOOGLE_ICON_RED_PATH} />
      <path fill="#4285F4" d={GOOGLE_ICON_BLUE_PATH} />
      <path fill="#FBBC05" d={GOOGLE_ICON_YELLOW_PATH} />
      <path fill="#34A853" d={GOOGLE_ICON_GREEN_PATH} />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={BULLRUN_COLORS.textSecondary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  );
}

/* ─── Animations ─── */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* ─── Styled Components ─── */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 200;
  animation: ${fadeIn} 0.15s ease;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 420px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: #212028,
  border: 1px solid ${BULLRUN_COLORS.borderSecondary};
  border-radius: 16px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  z-index: 201;
  animation: ${slideUp} 0.2s ease;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalHeader = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const ModalTitle = styled.h2({
  fontSize: "18px",
  fontWeight: 700,
  color: BULLRUN_COLORS.textPrimary,
  margin: 0,
  flex: 1,
  textAlign: "center",
});

const HeaderSpacer = styled.div({ width: "32px" });

const BackButton = styled.button({
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "none",
  background: "transparent",
  color: BULLRUN_COLORS.textSecondary,
  fontSize: "18px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.15s",
  ":hover": {
    background: BULLRUN_COLORS.bgHoverButton,
  },
});

const CloseButton = styled.button({
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "none",
  background: "transparent",
  color: BULLRUN_COLORS.textTertiary,
  fontSize: "14px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.15s, color 0.15s",
  ":hover": {
    background: BULLRUN_COLORS.bgHoverButton,
    color: BULLRUN_COLORS.textSecondary,
  },
});

const ErrorBanner = styled.div({
  background: "rgba(255, 107, 107, 0.1)",
  border: `1px solid ${BULLRUN_COLORS.accentAlertMuted}`,
  borderRadius: "10px",
  padding: "10px 14px",
  fontSize: "13px",
  color: BULLRUN_COLORS.accentAlert,
  lineHeight: 1.4,
});

const OptionsContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
});

const AuthOption = styled.button({
  display: "flex",
  alignItems: "center",
  gap: "14px",
  width: "100%",
  padding: "14px 16px",
  background: BULLRUN_COLORS.bgButton,
  border: `1px solid ${BULLRUN_COLORS.borderSecondary}`,
  borderRadius: "12px",
  cursor: "pointer",
  transition: "background 0.15s, border-color 0.15s",
  "&:hover:not(:disabled)": {
    background: BULLRUN_COLORS.bgHoverButton,
    borderColor: BULLRUN_COLORS.borderPrimary,
  },
  ":disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

const OptionIcon = styled.div({
  width: "36px",
  height: "36px",
  borderRadius: "10px",
  background: BULLRUN_COLORS.bgArea,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const EmailIcon = styled.span({
  fontSize: "16px",
  fontWeight: 700,
  color: BULLRUN_COLORS.textSecondary,
});

const OptionLabel = styled.span({
  fontSize: "15px",
  fontWeight: 600,
  color: BULLRUN_COLORS.textPrimary,
});

const FormContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "14px",
});

const InputLabel = styled.label({
  fontSize: "14px",
  fontWeight: 600,
  color: BULLRUN_COLORS.textSecondary,
  lineHeight: 1.5,
});

const EmailHighlight = styled.span({
  color: BULLRUN_COLORS.accentPrimary,
});

const TextInput = styled.input<{ $mono?: boolean }>(({ $mono }) => ({
  width: "100%",
  height: "48px",
  padding: "0 16px",
  background: BULLRUN_COLORS.bgArea,
  border: `1px solid ${BULLRUN_COLORS.borderSecondary}`,
  borderRadius: "10px",
  color: BULLRUN_COLORS.textPrimary,
  fontSize: $mono ? "20px" : "15px",
  fontWeight: 600,
  fontFamily: $mono ? WEB_FONT_STACK_MONO : "inherit",
  letterSpacing: $mono ? "6px" : "normal",
  textAlign: $mono ? "center" : "left",
  outline: "none",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
  "::placeholder": {
    color: BULLRUN_COLORS.textTertiary,
    letterSpacing: $mono ? "6px" : "normal",
  },
  ":focus": {
    borderColor: BULLRUN_COLORS.accentPrimary,
  },
  ":disabled": {
    opacity: 0.5,
  },
}));

const PrimaryButton = styled.button({
  width: "100%",
  height: "48px",
  borderRadius: "10px",
  border: "none",
  background: BULLRUN_COLORS.accentPrimary,
  color: BULLRUN_COLORS.black,
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer",
  transition: "background 0.15s, opacity 0.15s",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ":hover:not(:disabled)": {
    background: BULLRUN_COLORS.accentPrimaryLight,
  },
  ":disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
  },
});

const ResendRow = styled.div({
  display: "flex",
  justifyContent: "center",
});

const ResendButton = styled.button({
  background: "none",
  border: "none",
  color: BULLRUN_COLORS.textTertiary,
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: "6px",
  transition: "color 0.15s",
  ":hover:not(:disabled)": {
    color: BULLRUN_COLORS.accentPrimary,
  },
  ":disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

const PhoneInputRow = styled.div({
  display: "flex",
  gap: "8px",
  alignItems: "stretch",
});

const CountryPickerButton = styled.button({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "0 12px",
  height: "48px",
  background: BULLRUN_COLORS.bgArea,
  border: `1px solid ${BULLRUN_COLORS.borderSecondary}`,
  borderRadius: "10px",
  cursor: "pointer",
  color: BULLRUN_COLORS.textPrimary,
  fontSize: "14px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  transition: "border-color 0.15s",
  ":hover:not(:disabled)": {
    borderColor: BULLRUN_COLORS.borderPrimary,
  },
  ":disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

const DropdownArrow = styled.span({
  fontSize: "8px",
  color: BULLRUN_COLORS.textTertiary,
  marginLeft: "2px",
});

const CountryDropdown = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  background: BULLRUN_COLORS.bgArea,
  border: `1px solid ${BULLRUN_COLORS.borderSecondary}`,
  borderRadius: "12px",
  padding: "12px",
  height: "320px",
});

const CountrySearchInput = styled.input({
  width: "100%",
  height: "44px",
  padding: "0 14px",
  background: BULLRUN_COLORS.bgButton,
  border: `1px solid ${BULLRUN_COLORS.borderSecondary}`,
  borderRadius: "10px",
  color: BULLRUN_COLORS.textPrimary,
  fontSize: "14px",
  fontWeight: 500,
  outline: "none",
  flexShrink: 0,
  boxSizing: "border-box",
  transition: "border-color 0.15s",
  "::placeholder": {
    color: BULLRUN_COLORS.textTertiary,
  },
  ":focus": {
    borderColor: BULLRUN_COLORS.accentPrimary,
  },
});

const CountryList = styled.div({
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: 1,
  minHeight: 0,
});

const CountryItem = styled.button<{ $active?: boolean }>(({ $active }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px 10px",
  background: $active ? BULLRUN_COLORS.bgHoverButton : "transparent",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  color: BULLRUN_COLORS.textPrimary,
  fontSize: "13px",
  fontWeight: 500,
  textAlign: "left",
  transition: "background 0.12s",
  "&:hover": {
    background: BULLRUN_COLORS.bgHoverButton,
  },
}));

const CountryDialCode = styled.span({
  fontSize: "12px",
  color: BULLRUN_COLORS.textTertiary,
  fontWeight: 600,
});

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${BULLRUN_COLORS.borderSecondary};
  border-top-color: ${BULLRUN_COLORS.black};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;
